import { api, apiBlob, type Query } from './client';
import type {
  Activity,
  ApplicantPatch,
  BankAccount,
  BankAccountIn,
  Check,
  CheckCreate,
  CheckStatus,
  CoinRules,
  CoinRulesIn,
  LedgerReason,
  Page,
  PassportExtraction,
  Pricing,
  PasswordResetRequest,
  Purchase,
  PurchaseStatus,
  Quote,
  RegisterIn,
  ScreenshotApproveIn,
  ScreenshotStatus,
  Stats,
  Token,
  Transaction,
  TransliterateOut,
  ArabicCheckOut,
  User,
} from './types';

const V1 = '/api/v1';
const q = (o: object): Query => o as Query;

export const authApi = {
  register: (body: RegisterIn) => api<User>(`${V1}/auth/register`, { json: body, auth: false }),
  login: (username: string, password: string) =>
    api<Token>(`${V1}/auth/token`, { form: new URLSearchParams({ username, password }), auth: false }),
  me: () => api<User>(`${V1}/auth/me`),
  changePassword: (current_password: string, new_password: string) =>
    api<void>(`${V1}/auth/password/change`, { method: 'POST', json: { current_password, new_password } }),
  requestPasswordReset: (body: { email: string; phone: string; full_name: string }) =>
    api<{ status: string; message: string }>(`${V1}/auth/password-reset-requests`, { json: body, auth: false }),
};

export const coinsApi = {
  pricing: () => api<Pricing>(`${V1}/coins/pricing`),
  quote: (coins: number, signal?: AbortSignal) => api<Quote>(`${V1}/coins/quote`, { query: { coins }, signal }),
  createPurchase: (input: {
    coins: number;
    bank_account_id: string;
    receipt: File;
    transaction_ref?: string;
    note?: string;
  }) => {
    const form = new FormData();
    form.append('coins', String(input.coins));
    form.append('bank_account_id', input.bank_account_id);
    form.append('receipt', input.receipt);
    if (input.transaction_ref) form.append('transaction_ref', input.transaction_ref);
    if (input.note) form.append('note', input.note);
    return api<Purchase>(`${V1}/coins/purchases`, { form });
  },
  purchases: (p: Page & { status?: PurchaseStatus }) => api<Purchase[]>(`${V1}/coins/purchases`, { query: q(p) }),
  purchase: (id: string) => api<Purchase>(`${V1}/coins/purchases/${id}`),
  /** `receipt_url` from a Purchase. Needs the Bearer header, so it comes back as a Blob. */
  receipt: (receiptUrl: string) => apiBlob(receiptUrl),
  transactions: (p: Page) => api<Transaction[]>(`${V1}/coins/transactions`, { query: q(p) }),
};

export const checksApi = {
  create: (body: CheckCreate) => api<Check>(`${V1}/checks`, { json: body }),
  list: (p: Page & { status?: CheckStatus }) => api<Check[]>(`${V1}/checks`, { query: q(p) }),
  get: (id: string) => api<Check>(`${V1}/checks/${id}`),
  retry: (id: string) => api<Check>(`${V1}/checks/${id}/retry`, { method: 'POST' }),
  /** Stop a queued or running check; its coins are refunded. */
  cancel: (id: string) => api<Check>(`${V1}/checks/${id}/cancel`, { method: 'POST' }),
  updateApplicant: (id: string, body: ApplicantPatch) =>
    api<Check>(`${V1}/checks/${id}/applicant`, { method: 'PATCH', json: body }),
  uploadPhoto: (id: string, photo: File) => {
    const form = new FormData();
    form.append('photo', photo);
    return api<Check>(`${V1}/checks/${id}/photo`, { method: 'PUT', form });
  },
  deletePhoto: (id: string) => api<void>(`${V1}/checks/${id}/photo`, { method: 'DELETE' }),
  /** For a failed check: a screenshot of the portal's result, for an admin to read. */
  uploadScreenshot: (id: string, screenshot: File) => {
    const form = new FormData();
    form.append('screenshot', screenshot);
    return api<Check>(`${V1}/checks/${id}/screenshot`, { method: 'PUT', form });
  },
  /** `photo_url` / `report_url` from a Check. They need the Bearer header, so they come back as Blobs. */
  file: (url: string) => apiBlob(url),
};

export const passportsApi = {
  /** Reads a passport scan. Free, 20 per hour; the scan isn't stored. */
  extract: (scan: File, signal?: AbortSignal) => {
    const form = new FormData();
    form.append('scan', scan);
    return api<PassportExtraction>(`${V1}/passports/extract`, { form, signal });
  },
  transliterate: (text: string) => api<TransliterateOut>(`${V1}/transliterate`, { json: { text } }),
  /** The AI reviews an Arabic spelling of `text`; `checked` is false when it's unavailable. */
  checkArabic: (text: string, arabic: string) =>
    api<ArabicCheckOut>(`${V1}/transliterate/check`, { json: { text, arabic } }),
};

export const healthApi = {
  ok: async () => {
    try {
      await api<unknown>('/health', { auth: false });
      return true;
    } catch {
      return false;
    }
  },
};

export const adminApi = {
  stats: () => api<Stats>(`${V1}/admin/stats`),
  passwordResetRequests: () => api<PasswordResetRequest[]>(`${V1}/admin/password-reset-requests`),
  resetRequestedPassword: (id: string) =>
    api<PasswordResetRequest>(`${V1}/admin/password-reset-requests/${id}/reset`, { method: 'POST' }),
  settings: () => api<CoinRules>(`${V1}/admin/settings`),
  updateSettings: (body: CoinRulesIn) => api<CoinRules>(`${V1}/admin/settings`, { method: 'PUT', json: body }),

  bankAccounts: () => api<BankAccount[]>(`${V1}/admin/bank-accounts`),
  createBankAccount: (body: BankAccountIn) => api<BankAccount>(`${V1}/admin/bank-accounts`, { json: body }),
  updateBankAccount: (id: string, body: Partial<BankAccountIn>) =>
    api<BankAccount>(`${V1}/admin/bank-accounts/${id}`, { method: 'PATCH', json: body }),

  purchases: (p: Page & { status?: PurchaseStatus; user_id?: string }) =>
    api<Purchase[]>(`${V1}/admin/purchases`, { query: q(p) }),
  purchase: (id: string) => api<Purchase>(`${V1}/admin/purchases/${id}`),
  approve: (id: string, body: { coins?: number; note?: string }) =>
    api<Purchase>(`${V1}/admin/purchases/${id}/approve`, { json: body }),
  decline: (id: string, reason: string) => api<Purchase>(`${V1}/admin/purchases/${id}/decline`, { json: { reason } }),

  users: (p: Page & { search?: string; is_admin?: boolean; is_active?: boolean }) =>
    api<User[]>(`${V1}/admin/users`, { query: q(p) }),
  user: (id: string) => api<User>(`${V1}/admin/users/${id}`),
  updateUser: (id: string, body: Partial<Pick<User, 'full_name' | 'phone' | 'email' | 'is_active' | 'is_admin'>>) =>
    api<User>(`${V1}/admin/users/${id}`, { method: 'PATCH', json: body }),
  adjustCoins: (id: string, delta: number, note: string) =>
    api<User>(`${V1}/admin/users/${id}/coins`, { json: { delta, note } }),

  checks: (p: Page & { user_id?: string; status?: CheckStatus; screenshot_status?: ScreenshotStatus }) =>
    api<Check[]>(`${V1}/admin/checks`, { query: q(p) }),
  approveScreenshot: (id: string, body: ScreenshotApproveIn) =>
    api<Check>(`${V1}/admin/checks/${id}/screenshot/approve`, { json: body }),
  declineScreenshot: (id: string, reason: string) =>
    api<Check>(`${V1}/admin/checks/${id}/screenshot/decline`, { json: { reason } }),
  transactions: (p: Page & { user_id?: string; reason?: LedgerReason }) =>
    api<Transaction[]>(`${V1}/admin/transactions`, { query: q(p) }),
  activity: (p: Page & { user_id?: string; action?: string }) =>
    api<Activity[]>(`${V1}/admin/activity`, { query: q(p) }),
};
